// Gantt filters live in the URL (constitution §5.6), so they survive reload and the back button.
import { isZoom, type Zoom } from './gantt-scale.ts';

export interface GanttSearch {
  readonly phase?: string;
  readonly area?: string;
  readonly status?: string;
  readonly mine?: boolean;
  readonly zoom?: Zoom;
}

// Phase code: matches "P0" to "P4"; rejects "P5", "p1".
// https://regex101.com/?flavor=javascript&regex=%5EP%5B0-4%5D%24&testString=P0%0AP4%0AP5%0Ap1
const PHASE = /^P[0-4]$/;
// Short label from the pack (area): matches "OPS", "IT Support"; rejects "<script>", "".
// https://regex101.com/?flavor=javascript&regex=%5E%5B%5Cw%20%26%2F-%5D%7B1%2C64%7D%24&testString=OPS%0AIT%20Support%0A%3Cscript%3E
const LABEL = /^[\w &/-]{1,64}$/;
const STATUSES = ['not_started', 'in_progress', 'blocked', 'done', 'verified', 'deferred'];

const text = (v: unknown, re: RegExp): string | undefined =>
  typeof v === 'string' && re.test(v) ? v : undefined;

/** Keeps only valid values; anything else in the URL is dropped, never sent to the API. */
export function parseGanttSearch(raw: Readonly<Record<string, unknown>>): GanttSearch {
  const status =
    typeof raw['status'] === 'string' && STATUSES.includes(raw['status'])
      ? raw['status']
      : undefined;
  const out: Record<string, unknown> = {
    phase: text(raw['phase'], PHASE),
    area: text(raw['area'], LABEL),
    status,
    mine: raw['mine'] === true || raw['mine'] === 'true' ? true : undefined,
    zoom: isZoom(raw['zoom']) ? raw['zoom'] : undefined,
  };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined)) as GanttSearch;
}

/** The API query for the filters (zoom is display only). */
export function ganttApiQuery(s: GanttSearch): Readonly<Record<string, string | undefined>> {
  return {
    phase: s.phase,
    area: s.area,
    status: s.status,
    assignee: s.mine === true ? 'me' : undefined,
  };
}
